import { AxiosInstance } from 'axios';
import AxiosFlexeServeInstance from '../../shared/AxiosFlexeServe';
import confirmCorrelationIdRecursive from '../../shared/confirmCorrelationIdRecursive';
import {
  AirConnectNode,
  AirConnectNodeScanned,
  AirConnectNodeTypes,
  ControlUnitInstallation,
} from './CommanderAirConnectAPI.model';
import Environment from '../../../shared/constants/Environment';
import { waitPromise } from '../../../shared/util/waitPromise';
import recursiveExponentialBackOff from '../../shared/recursiveExponentialBackOff';
import { isNotNullOrUndefined } from '../../../shared/util/collection/isNotNullOrUndefined';
import FlexeserveDeviceAPI from '../FlexeserveDevice/FlexeserveDeviceAPI';
import { fromBase64 } from '../../../shared/util/base64';
import store from '../../../redux/Store';
import _ from 'lodash';

export class CommanderAirConnectAPIService {
  private readonly baseUrl = Environment.backendAPICommander;

  public constructor(private axios: AxiosInstance = AxiosFlexeServeInstance) {}

  private getNodesInit = (encodedCommanderId: string): Promise<any> => {
    return this.axios({
      url: `${this.baseUrl}/${encodedCommanderId}/configuration/control-unit-management`,
      method: 'post',
      data: {
        subtype: 'AC_START_SCAN',
      },
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodedCommanderId,
          correlationID,
          this.getConfirmation
        )
      )
      .then((data) => data?.response?.result);
  };

  private getNodesResultScanned = (
    encodedDeviceId: string
  ): Promise<AirConnectNodeScanned[]> => {
    return this.axios({
      url: `${this.baseUrl}/${encodedDeviceId}/configuration/control-unit-management`,
      method: 'post',
      data: {
        subtype: 'AC_SCAN_RESULTS',
      },
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodedDeviceId,
          correlationID,
          this.getConfirmation
        )
      )
      .then((data) => data?.response?.result?.scanResults);
  };

  getNodesAvailableToPair = (
    encodedCommanderId: string
  ): Promise<AirConnectNodeScanned[]> => {
    return this.getNodesInit(encodedCommanderId).then(() =>
      this.getNodesResultScanned(encodedCommanderId)
    );
  };

  getNodesInstalled = (
    encodedCommanderId: string
  ): Promise<AirConnectNode[]> => {
    return this.axios({
      url: `${this.baseUrl}/${encodedCommanderId}/configuration/control-unit-management`,
      method: 'post',
      data: {
        subtype: 'AC_INSTALLED_NODES',
      },
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodedCommanderId,
          correlationID,
          this.getConfirmation
        )
      )
      .then((data) => data?.response?.result?.installedNodes);
  };

  installNode = (encodedCommanderId: string, macAddress: string) => {
    return this.axios({
      url: `${this.baseUrl}/${encodedCommanderId}/configuration/control-unit-management`,
      method: 'post',
      data: {
        subtype: 'AC_INSTALL_NODE',
        nodeMacAddressToInstall: macAddress,
      },
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodedCommanderId,
          correlationID,
          this.getConfirmation
        )
      )
      .then((data) => data?.response?.result);
  };

  deleteNode = (encodedCommanderId: string, macAddress: string) => {
    return this.axios({
      url: `${this.baseUrl}/${encodedCommanderId}/configuration/control-unit-management`,
      method: 'post',
      data: {
        subtype: 'AC_REMOVE_NODE_BY_MAC_ADDRESS',
        nodeMacAddressToRemove: macAddress,
      },
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodedCommanderId,
          correlationID,
          this.getConfirmation
        )
      )
      .then((data) => data?.response?.result);
  };

  saveConfigurationNodes = (
    encodedCommanderId: string,
    node: AirConnectNode
  ) => {
    return this.axios({
      url: `${this.baseUrl}/${encodedCommanderId}/configuration/control-unit-management`,
      method: 'post',
      data: {
        subtype: 'AC_SAVE_NODE_CONTROLLERS_CONFIG',
        nodeNumber: node.nodeNumber,
      },
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodedCommanderId,
          correlationID,
          this.getConfirmation
        )
      )
      .then((data) => data?.response?.result);
  };

  installNodes = async (encodedCommanderId: string, macAddress: string[]) => {
    if (!macAddress || macAddress.length === 0) {
      return Promise.resolve([]);
    }
    let lastNodeState: AirConnectNode[] = await this.getNodesInstalled(
      encodedCommanderId
    );

    /* Just install the mac addresses that are not installed yet */
    const macAddressToInstall = macAddress
      .filter((mac) => !this.isNodeInstalled(lastNodeState, mac))
      .filter(isNotNullOrUndefined);

    await waitPromise(1000 * 2); // Wait 2 seconds
    for (const mac of macAddressToInstall) {
      await this.installNode(encodedCommanderId, mac);
      await waitPromise(1000 * 10); // Wait 10 seconds
    }
    lastNodeState = await this.getNodesInstalled(encodedCommanderId);
    const installedNodes = macAddress.filter((mac) =>
      this.isNodeInstalled(lastNodeState, mac)
    );
    if (installedNodes.length === 0) {
      throw new Error('initial_setup.connect_commander.error_installing_nodes');
    }

    await waitPromise(1000 * 20); // Wait another 20 seconds
    await recursiveExponentialBackOff(
      () => this.getNodesInstalled(encodedCommanderId),
      this.isNodesReachable
    );

    /* Send all nodes available to reinstall missing CU */
    return this.installNewAndMissingControlUnits(
      encodedCommanderId,
      lastNodeState
    );
  };

  isNodeInstalled = (nodes: AirConnectNode[], macAddress: string): boolean => {
    const foundNode = nodes.find((n) => n.macAddress === macAddress);
    return !!foundNode;
  };

  isNodesReachable = (nodes: AirConnectNode[]) => {
    return nodes.every(this.isNodeReachable);
  };

  isNodeReachable = (node: AirConnectNode) => {
    return node.nodeReachable && !Number.isNaN(node.status)
      ? node.status !== 0
      : true;
  };

  isNodeRemoved = (nodes: AirConnectNode[], node: AirConnectNode) => {
    const foundNode = nodes.find(
      (n) =>
        n.macAddress === node.macAddress && n.nodeNumber === node.nodeNumber
    );
    return !foundNode;
  };

  reInstallControlUnitsFromNode = async (
    encodedCommanderId: string,
    nodeNumber: number,
    nodeType: string
  ) => {
    let lastNodeState: AirConnectNode[] = await this.getNodesInstalled(
      encodedCommanderId
    );
    let node = lastNodeState.find((node) => node.nodeNumber === nodeNumber);
    if (!node) throw new Error('Node not found');
    await this.unInstallControlUnits(encodedCommanderId, [node]);
    if (nodeType === '1')
      await this.factoryResetNode(encodedCommanderId, nodeNumber);
    await waitPromise(1000 * 20); // Wait 20 seconds
    lastNodeState = await recursiveExponentialBackOff(
      () => this.getNodesInstalled(encodedCommanderId),
      (response) => {
        const node = response.find((node) => node.nodeNumber === nodeNumber);
        return !!node && this.isNodeReachable(node);
      }
    );

    if (node?.nodeType === AirConnectNodeTypes.STAirConnectNode)
      await this.saveConfigurationNodes(encodedCommanderId, node);

    await waitPromise(1000 * 20); // Wait another 20 seconds
    lastNodeState = await recursiveExponentialBackOff(
      () => this.getNodesInstalled(encodedCommanderId),
      (response) => {
        const node = response.find((node) => node.nodeNumber === nodeNumber);
        return !!node?.nodeReachable;
      }
    );
    node = lastNodeState.find((node) => node.nodeNumber === nodeNumber);
    node && this.installControlUnits(encodedCommanderId, [node]);
    return node;
  };

  rebootNode = (encodeCommanderId: string, nodeNumber: number) => {
    return this.axios({
      url: `${this.baseUrl}/${encodeCommanderId}/configuration/control-unit-management`,
      method: 'post',
      data: {
        subtype: 'AC_REBOOT',
        nodeNumber,
      },
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodeCommanderId,
          correlationID,
          this.getConfirmation
        )
      )
      .then((data) => data?.response?.result);
  };

  factoryResetNode = (encodeCommanderId: string, nodeNumber: number) => {
    return this.axios({
      url: `${this.baseUrl}/${encodeCommanderId}/configuration/control-unit-management`,
      method: 'post',
      data: {
        subtype: 'AC_FACTORY_RESET',
        nodeNumber,
      },
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodeCommanderId,
          correlationID,
          this.getConfirmation
        )
      )
      .then((data) => data?.response?.result);
  };

  getConfirmation = (encodedCommanderId: string, correlationId: string) => {
    return this.axios({
      url: `${this.baseUrl}/${encodedCommanderId}/configuration/control-unit-management/response/${correlationId}`,
      method: 'get',
    }).then((response) => response.data);
  };

  inferNodesFromAddresses = (
    addresses: string[],
    nodes: AirConnectNode[]
  ): AirConnectNode[] => {
    return _.uniq(
      addresses
        .map((address) =>
          nodes.find(
            (node) => node.nodeNumber === Math.round(Number(address) / 8)
          )
        )
        .filter(isNotNullOrUndefined)
    );
  };

  installNewAndMissingControlUnits = (
    encodedCommanderId: string,
    lastNodeState: AirConnectNode[]
  ) => {
    return this.collectMissingAddressControlUnits(encodedCommanderId).then(
      (missingAddress) =>
        Promise.resolve().then(async () => {
          try {
            // check if the the missing address is already in the lastNodeState
            const nodes = this.inferNodesFromAddresses(
              missingAddress,
              lastNodeState
            );
            // and if was a nodeType == 1 saveNodeConfiguration
            const nodesToSave = nodes.filter(
              (node) => node.nodeType === AirConnectNodeTypes.STAirConnectNode
            );

            for (const node of nodesToSave) {
              await this.saveConfigurationNodes(encodedCommanderId, node);
              await waitPromise(1000 * 20); // Wait 20 seconds because is falling
            }

            return await this.installControlUnits(encodedCommanderId, nodes);
          } catch (error) {
            // skip the error and go to the next step
            // eslint-disable-next-line no-console
            console.error(
              'Error installing control units, skip an go to next step:',
              error
            );
          }
        })
    );
  };

  collectMissingAddressControlUnits = (encodedCommanderId: string) => {
    return store
      .dispatch(
        FlexeserveDeviceAPI.endpoints.getDeviceByFlexeserveDeviceId.initiate(
          fromBase64(encodedCommanderId)
        )
      )
      .unwrap()
      .then((flexeServeDevice) =>
        this.getNodesInstalled(encodedCommanderId)
          .then((nodesInstalled) =>
            this.inferAddressesFromNodes(nodesInstalled)
          )
          .then((addresses) =>
            // find if the address is not already in the flexeServeDevice
            addresses.filter(
              (address) =>
                !flexeServeDevice?.shelfMappings.some(
                  (controlUnitMap) => controlUnitMap?.cuaddr === address
                )
            )
          )
      );
  };

  inferAddressesFromNodes = (nodes: AirConnectNode[]): string[] => {
    return nodes
      .map((node) =>
        Array.from({ length: node.numberOfControllers }, (_, i) =>
          (node.nodeAddress + i).toString()
        )
      )
      .flat();
  };

  installControlUnits = (
    encodedCommanderId: string,
    nodes: AirConnectNode[],
    extraArgs: Partial<ControlUnitInstallation> = {}
  ) => {
    // map AirConnectNodeScanned to ControlUnitInstallation
    const inferAddresses = this.inferAddressesFromNodes(nodes);

    const addresses = _(inferAddresses)
      .concat(extraArgs.addresses ?? [])
      .uniq()
      .value();
    if (addresses.length === 0) return Promise.resolve();
    const controlUnitInstallation: ControlUnitInstallation = {
      ...extraArgs,
      addresses,
    };
    return this.axios({
      url: `${this.baseUrl}/${encodedCommanderId}/configuration/cabinet/installation-control-unit`,
      method: 'post',
      data: controlUnitInstallation,
    })
      .then((response) => response.data)
      .then(({ correlationID }) =>
        confirmCorrelationIdRecursive(
          encodedCommanderId,
          correlationID,
          this.getConfirmationInstallationControlUnit
        )
      )
      .then((data) => data?.response?.result);
  };

  unInstallControlUnits = (
    encodedCommanderId: string,
    nodesInstalled: AirConnectNode[]
  ) => {
    return this.installControlUnits(encodedCommanderId, nodesInstalled, {
      uninstall: true,
    });
  };

  getConfirmationInstallationControlUnit = (
    encodedCommanderId: string,
    correlationId: string
  ) => {
    return this.axios({
      url: `${this.baseUrl}/${encodedCommanderId}/configuration/cabinet/installation-control-unit/response/${correlationId}`,
      method: 'get',
    }).then((response) => response?.data);
  };

  removeNodeFromControlUnit = async (
    encodedCommanderId: string,
    node: AirConnectNode
  ): Promise<null> => {
    await this.deleteNode(encodedCommanderId, node.macAddress);
    await waitPromise(1000 * 2); // Wait 2 seconds

    await recursiveExponentialBackOff(
      () => this.getNodesInstalled(encodedCommanderId),
      (nodes) => this.isNodeRemoved(nodes, node)
    );

    await waitPromise(1000 * 2); // Wait 2 seconds

    const data = await this.unInstallControlUnits(encodedCommanderId, [node]);

    return data;
  };
}

export default new CommanderAirConnectAPIService();
